import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import type { Entry, Client, Config, SyncStatus } from "@ascente/shared-types";
import { api, type MutationResponse } from "../api/client.js";
import { getStoredAuth, setStoredAuth, clearStoredAuth } from "../utils/localStorage.js";
import { toDateInput } from "../utils/dateHelpers.js";
import type { EntrySort } from "../utils/entrySort.js";
import type { InvoiceDraft } from "../components/InvoicePreview/InvoicePreview.js";

const SYNC_INTERVAL_MS = 30_000;
const CLIENT_COLORS = [
  "#2563eb", "#7c3aed", "#059669", "#d97706",
  "#dc2626", "#0891b2", "#be185d", "#4f46e5",
];

export type Tab = "dashboard" | "analytics" | "entries" | "clients" | "settings";

/** The result of one write, before any of it reaches React state. */
type WriteOutcome = { ok: true } | { ok: false; message: string };

/**
 * Send one write and classify what came back.
 *
 * A write can fail three ways and only one of them throws: the request can
 * fail outright, or the request can succeed while the backend answers
 * `{ success: false, error }` — "Entry not found", say. Every handler used
 * to treat the second case as a success and report "Synced", so a delete
 * that silently did nothing looked exactly like one that worked.
 */
async function attemptWrite(
  fallback: string,
  send: () => Promise<MutationResponse>,
): Promise<WriteOutcome> {
  try {
    const res = await send();
    if (res.success) return { ok: true };
    const detail = res.error?.trim();
    return { ok: false, message: detail ? `${fallback} ${detail}` : fallback };
  } catch {
    return { ok: false, message: `${fallback} The server could not be reached.` };
  }
}

export interface AppState {
  // Auth
  isLoggedIn: boolean;
  userName: string;
  loginError: string;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;

  // Data
  entries: Entry[];
  clients: Client[];
  config: Partial<Config>;
  syncStatus: SyncStatus;
  /** Why the last write or load failed. Empty unless syncStatus is "error". */
  syncError: string;
  lastSync: Date | null;
  refreshData: () => Promise<void>;

  // UI
  activeTab: Tab;
  setActiveTab: (t: Tab) => void;
  darkMode: boolean;
  setDarkMode: (v: boolean) => void;

  // Dashboard date range
  rangeStart: string;
  setRangeStart: (v: string) => void;
  rangeEnd: string;
  setRangeEnd: (v: string) => void;

  // Entries tab
  entriesSortBy: EntrySort;
  setEntriesSortBy: (v: EntrySort) => void;
  entriesAuthorFilter: string;
  setEntriesAuthorFilter: (v: string) => void;
  allUsers: string[];

  // Analytics tab
  analyticsRange: string;
  setAnalyticsRange: (v: string) => void;
  analyticsCustomStart: string;
  setAnalyticsCustomStart: (v: string) => void;
  analyticsCustomEnd: string;
  setAnalyticsCustomEnd: (v: string) => void;

  // Entry management
  showAddEntry: boolean;
  setShowAddEntry: (v: boolean) => void;
  editEntry: Entry | null;
  setEditEntry: (e: Entry | null) => void;
  // Every write resolves to whether the server accepted it, so a caller
  // can avoid telling the user it saved when it did not.
  handleAddEntry: (draft: Omit<Entry, "id" | "person" | "addedBy">) => Promise<boolean>;
  handleUpdateEntry: (entry: Entry) => Promise<boolean>;
  handleDeleteEntry: (entryId: string) => Promise<boolean>;

  // Client management
  showEditClient: Client | null;
  setShowEditClient: (c: Client | null) => void;
  showAddClient: boolean;
  setShowAddClient: (v: boolean) => void;
  handleSaveClient: (client: Client) => Promise<boolean>;
  handleAddClient: (draft: Omit<Client, "id" | "color">) => Promise<boolean>;
  handleArchiveClient: (clientId: string) => Promise<boolean>;

  // Settings
  handleSaveSettings: (settings: Partial<Config>) => Promise<boolean>;

  // Invoice modal
  invoiceDraft: InvoiceDraft | null;
  setInvoiceDraft: (d: InvoiceDraft | null) => void;
}

const AppContext = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const today = new Date();
  const todayStr = toDateInput(today);

  // Auth
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");
  const [loginError, setLoginError] = useState("");

  // Data
  const [entries, setEntries] = useState<Entry[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [config, setConfig] = useState<Partial<Config>>({});
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("ok");
  const [syncError, setSyncError] = useState("");
  const [lastSync, setLastSync] = useState<Date | null>(null);

  // UI
  const [activeTab, setActiveTab] = useState<Tab>("dashboard");
  const [darkMode, setDarkMode] = useState(false);
  const [rangeStart, setRangeStart] = useState(
    toDateInput(new Date(today.getFullYear(), today.getMonth(), 1)),
  );
  const [rangeEnd, setRangeEnd] = useState(
    toDateInput(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
  );

  // Entries tab
  const [entriesSortBy, setEntriesSortBy] = useState<EntrySort>("date-desc");
  const [entriesAuthorFilter, setEntriesAuthorFilter] = useState("");

  // Analytics tab
  const [analyticsRange, setAnalyticsRange] = useState("this-month");
  const [analyticsCustomStart, setAnalyticsCustomStart] = useState("");
  const [analyticsCustomEnd, setAnalyticsCustomEnd] = useState("");

  // Entry modal
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [editEntry, setEditEntry] = useState<Entry | null>(null);

  // Client modal
  const [showEditClient, setShowEditClient] = useState<Client | null>(null);
  const [showAddClient, setShowAddClient] = useState(false);

  // Invoice modal
  const [invoiceDraft, setInvoiceDraft] = useState<InvoiceDraft | null>(null);

  const syncRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Derived data ─────────────────────────────────────────────────────────
  const allUsers = useMemo(() => {
    const users = new Set(entries.map((e) => e.addedBy).filter((u) => u !== ""));
    if (userName) users.add(userName);
    return [...users].sort();
  }, [entries, userName]);

  // ── Dark mode effect ──────────────────────────────────────────────────────
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  // ── Load all data ─────────────────────────────────────────────────────────
  const loadAll = useCallback(
    async (name: string) => {
      setSyncStatus("loading");
      try {
        const data = await api.loadAll(name);
        setEntries(data.entries ?? []);
        setClients(data.clients ?? []);
        setConfig(data.config ?? {});
        setSyncStatus("ok");
        setSyncError("");
        setLastSync(new Date());
      } catch {
        setSyncStatus("error");
        setSyncError("Could not load data from the server.");
      }
    },
    [],
  );

  const refreshData = useCallback(() => loadAll(userName), [loadAll, userName]);

  // ── Bootstrap on auth ─────────────────────────────────────────────────────
  useEffect(() => {
    const stored = getStoredAuth();
    if (stored) {
      setUserName(stored.displayName);
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !userName) return;
    void api.init();
    void loadAll(userName);
    syncRef.current = setInterval(() => void loadAll(userName), SYNC_INTERVAL_MS);
    return () => {
      if (syncRef.current) clearInterval(syncRef.current);
    };
  }, [isLoggedIn, userName, loadAll]);

  // ── Auth ──────────────────────────────────────────────────────────────────
  const login = async (username: string, password: string) => {
    setLoginError("");
    try {
      const res = await api.login(username, password);
      if (res.success && res.username && res.displayName) {
        setStoredAuth({ username: res.username, displayName: res.displayName });
        setUserName(res.displayName);
        setIsLoggedIn(true);
      } else {
        setLoginError(res.error ?? "Invalid credentials");
      }
    } catch {
      setLoginError("Connection error. Please try again.");
    }
  };

  const logout = () => {
    clearStoredAuth();
    setIsLoggedIn(false);
    setUserName("");
    setEntries([]);
    setClients([]);
    setConfig({});
    if (syncRef.current) clearInterval(syncRef.current);
  };

  // ── Write plumbing ────────────────────────────────────────────────────────
  /** Push one write outcome into the sync indicator. Returns whether it worked. */
  const reportWrite = useCallback((outcome: WriteOutcome): boolean => {
    if (outcome.ok) {
      setSyncStatus("ok");
      setSyncError("");
      return true;
    }
    setSyncStatus("error");
    setSyncError(outcome.message);
    return false;
  }, []);

  /** Send a write and report the outcome in one step. */
  const runWrite = useCallback(
    async (fallback: string, send: () => Promise<MutationResponse>): Promise<boolean> =>
      reportWrite(await attemptWrite(fallback, send)),
    [reportWrite],
  );

  // ── Entry handlers ────────────────────────────────────────────────────────
  const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

  const handleAddEntry = async (draft: Omit<Entry, "id" | "person" | "addedBy">) => {
    const entry: Entry = { ...draft, id: uid(), person: userName, addedBy: userName };
    setEntries((prev) => [...prev, entry]);
    setShowAddEntry(false);
    return runWrite("Could not save the entry.", () => api.addEntry(entry));
  };

  const handleUpdateEntry = async (entry: Entry) => {
    setEntries((prev) => prev.map((e) => (e.id === entry.id ? entry : e)));
    setEditEntry(null);
    setShowAddEntry(false);
    return runWrite("Could not update the entry.", () => api.updateEntry(entry));
  };

  const handleDeleteEntry = async (entryId: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== entryId));
    setSyncStatus("loading");
    const outcome = await attemptWrite("Could not delete the entry.", () =>
      api.deleteEntry(entryId),
    );
    // Put the row back before reporting, so the message and the list agree.
    // loadAll clears the error on success, hence the order: resync first,
    // then report.
    if (!outcome.ok) await loadAll(userName);
    return reportWrite(outcome);
  };

  // ── Client handlers ───────────────────────────────────────────────────────
  const handleSaveClient = async (updated: Client) => {
    const next = clients.map((c) => (c.id === updated.id ? updated : c));
    setClients(next);
    setShowEditClient(null);
    return runWrite("Could not save the client list.", () => api.saveClients(next));
  };

  const handleAddClient = async (draft: Omit<Client, "id" | "color">) => {
    const client: Client = {
      ...draft,
      id: uid(),
      color: CLIENT_COLORS[clients.length % CLIENT_COLORS.length] ?? "#2563eb",
    };
    const next = [...clients, client];
    setClients(next);
    setShowAddClient(false);
    return runWrite("Could not save the client list.", () => api.saveClients(next));
  };

  const handleArchiveClient = async (clientId: string) => {
    const next = clients.map((c) =>
      c.id === clientId ? { ...c, status: "archived" as const } : c,
    );
    setClients(next);
    setShowEditClient(null);
    return runWrite("Could not archive the client.", () => api.saveClients(next));
  };

  // ── Settings ──────────────────────────────────────────────────────────────
  const handleSaveSettings = async (settings: Partial<Config>) => {
    setConfig((prev) => ({ ...prev, ...settings }));
    return runWrite("Could not save your settings.", () =>
      api.saveUserSettings(userName, settings),
    );
  };

  // Suppress unused var warning for todayStr
  void todayStr;

  return (
    <AppContext.Provider
      value={{
        isLoggedIn, userName, loginError, login, logout,
        entries, clients, config, syncStatus, syncError, lastSync, refreshData,
        activeTab, setActiveTab, darkMode, setDarkMode,
        rangeStart, setRangeStart, rangeEnd, setRangeEnd,
        entriesSortBy, setEntriesSortBy, entriesAuthorFilter, setEntriesAuthorFilter, allUsers,
        analyticsRange, setAnalyticsRange,
        analyticsCustomStart, setAnalyticsCustomStart,
        analyticsCustomEnd, setAnalyticsCustomEnd,
        showAddEntry, setShowAddEntry, editEntry, setEditEntry,
        handleAddEntry, handleUpdateEntry, handleDeleteEntry,
        showEditClient, setShowEditClient, showAddClient, setShowAddClient,
        handleSaveClient, handleAddClient, handleArchiveClient,
        handleSaveSettings,
        invoiceDraft, setInvoiceDraft,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp(): AppState {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
}
